import React, { useEffect, useState } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import {
  Paper,
  Typography,
  Box,
  Button,
  Alert,
  AlertTitle,
  CircularProgress,
} from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import * as XLSX from "xlsx";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import jsPDF from "jspdf";
import "jspdf-autotable";
import { getFirestore, collection, getDocs } from "firebase/firestore";
import RefreshIcon from '@mui/icons-material/Refresh';
import { useAuth } from "../auth-context";


const TodayInvoicesGrid = ({ invoices = [] }) => {
  const { userRole } = useAuth();
  const isChef = userRole === 'chef';

  const theme = React.useMemo(() => createTheme({
    palette: {
      primary: {
        main: isChef ? "#2E7D32" : "#dc2626",
      },
      secondary: {
        main: isChef ? "#4CAF50" : "#eab308",
      },
    },
  }), [isChef]);
  const [items, setItems] = useState({});
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState({});
  // State to track scroll position
  const [scrollPosition, setScrollPosition] = useState(0);
  const [inventoryItems, setInventoryItems] = useState([]);

  // **ADD THESE HELPER FUNCTIONS AFTER THE IMPORTS AND BEFORE THE COMPONENT**

  const inventoryItemsMap = inventoryItems.reduce((map, invItem) => {
    map[invItem.id] = invItem;
    return map;
  }, {});


  // UK Time helper functions
  const getUKTime = (date = new Date()) => {
    return new Date(date.toLocaleString("en-US", { timeZone: "Europe/London" }));
  };

  const formatUKTime = (timestamp) => {
    if (!timestamp) return 'N/A';

    let date;
    if (timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000);
    } else {
      date = new Date(timestamp);
    }

    return getUKTime(date).toLocaleString("en-GB", {
      timeZone: "Europe/London",
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTodayRangeText = () => {
    const ukNow = getUKTime();
    const todayStart = new Date(ukNow);
    todayStart.setHours(9, 0, 0, 0);

    if (ukNow.getHours() < 9) {
      todayStart.setDate(todayStart.getDate() - 1);
    }

    const todayEnd = new Date(todayStart);
    todayEnd.setDate(todayEnd.getDate() + 1);
    todayEnd.setHours(9, 0, 0, 0);

    return {
      start: todayStart.toLocaleDateString("en-GB", {
        timeZone: "Europe/London",
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      }),
      end: todayEnd.toLocaleDateString("en-GB", {
        timeZone: "Europe/London",
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      })
    };
  };

  useEffect(() => {
    const fetchInventoryItems = async () => {
      const db = getFirestore();
      const inventorySnapshot = await getDocs(collection(db, "inventoryItems"));
      const inventoryData = [];
      inventorySnapshot.forEach(doc => {
        inventoryData.push({ id: doc.id, ...doc.data() });
      });
      setInventoryItems(inventoryData);
    };

    fetchInventoryItems();
  }, []);
  console.log(inventoryItems, "InventoryItems");

  useEffect(() => {
    const fetchCategories = async () => {
      const db = getFirestore();
      const categoriesSnapshot = await getDocs(collection(db, "inventoryCategory"));
      const categoriesData = {};

      categoriesSnapshot.forEach((doc) => {
        categoriesData[doc.id] = doc.data().category;
      });

      setCategories(categoriesData);
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    if (invoices && invoices.length > 0 && Object.keys(categories).length > 0) {
      processInvoices();
    }
  }, [invoices, categories]);

  // Add effect to update category headers position when scrolling
  useEffect(() => {
    const categoryHeaders = document.querySelectorAll('.category-label');
    categoryHeaders.forEach(header => {
      if (header) {
        header.style.transform = `translateX(${scrollPosition}px)`;
      }
    });
  }, [scrollPosition]);

  // **ADD THIS REFRESH FUNCTION**
  const handleRefresh = async () => {
    console.log("🔄 Refreshing Today's Invoices Grid...");
    setLoading(true);

    try {
      // **Re-fetch categories** 
      const db = getFirestore();
      const categoriesSnapshot = await getDocs(collection(db, "inventoryCategory"));
      const categoriesData = {};

      categoriesSnapshot.forEach((doc) => {
        categoriesData[doc.id] = doc.data().category;
      });

      console.log("🏷️ Categories refreshed:", Object.keys(categoriesData).length);
      setCategories(categoriesData);

      // **Re-process invoices with fresh data**
      if (invoices && invoices.length > 0) {
        await processInvoices();
        console.log("✅ Invoices grid refreshed successfully");
      } else {
        console.log("⚠️ No invoices to refresh");
      }

    } catch (error) {
      console.error("❌ Error refreshing invoices grid:", error);
    } finally {
      setLoading(false);
    }
  };


  // Handle scroll event
  const handleScroll = (e) => {
    setScrollPosition(e.target.scrollLeft);
  };

  const processInvoices = async () => {
    try {
      setLoading(true);

      // Collect all unique items across invoices
      const uniqueItems = new Set();
      invoices.forEach((invoice) =>
        invoice.items.forEach((item) => uniqueItems.add(item.title))
      );

      const itemList = Array.from(uniqueItems).map((title) => ({ title }));

      // Get unique restaurant names
      const restaurantNames = [...new Set(invoices.map(invoice => invoice.restaurantName))];

      // Generate restaurant-wise item quantities
      // Generate restaurant-wise item quantities
      const restaurantGrid = restaurantNames.map((restaurantName) => {
        const orders = invoices.filter(
          (invoice) => invoice.restaurantName === restaurantName
        );

        const itemQuantities = itemList.map(item => {
          // Combine quantities from THIS RESTAURANT'S orders only
          let quantity = 0;
          let actualQuantity = null;
          let actualQuantityUnit = null;

          orders.forEach(order => {  // ✅ FIX: Using filtered orders for this restaurant
            order.items.forEach(orderItem => {
              if (orderItem.title === item.title) {
                quantity += orderItem.quantity || 0;

                // Lookup inventory item by refId
                const inventoryItem = inventoryItemsMap[orderItem.refId];
                if (inventoryItem && inventoryItem.oneUnitQuantity > 0) {
                  actualQuantity = (actualQuantity || 0) + (orderItem.quantity * inventoryItem.oneUnitQuantity);
                  actualQuantityUnit = inventoryItem.oneUnitQuantityUnit || inventoryItem.units;
                }
              }
            });
          });

          return { title: item.title, quantity, actualQuantity, actualQuantityUnit };
        });

        return { restaurantName, itemQuantities };
      });

      // Categorize items using categoryId from items
      const categorizedItems = {};

      invoices.forEach((invoice) => {
        invoice.items.forEach((item) => {
          // Get category name from categories map using categoryId
          const categoryName = categories[item.categoryId] || "Uncategorized";

          if (!categorizedItems[categoryName]) {
            categorizedItems[categoryName] = [];
          }

          if (!categorizedItems[categoryName].some((i) => i.title === item.title)) {
            categorizedItems[categoryName].push(item);
          }
        });
      });

      const sortedCategories = Object.keys(categorizedItems).sort();

      // Create new object with categories sorted
      const sortedCategorizedItems = {};
      sortedCategories.forEach(category => {
        // Sort items in each category alphabetically by title
        sortedCategorizedItems[category] = categorizedItems[category].sort((a, b) =>
          a.title.localeCompare(b.title)
        );
      });

      setItems(sortedCategorizedItems);
      setData(restaurantGrid);
    } catch (error) {
      console.error("Error processing invoices: ", error);
    } finally {
      setLoading(false);
    }
  };

  const convertToMainUnit = (quantity, unit) => {
    // Units conversion map: key is main unit, values are subunit conversion rates
    const conversionMap = {
      kg: { subunits: ["g", "gram", "grams"], factor: 1000 },
      litre: { subunits: ["ml", "milliliter", "milliliters"], factor: 1000 },
      // Add other unit conversions as required
    };

    for (const mainUnit in conversionMap) {
      const { subunits, factor } = conversionMap[mainUnit];
      if (subunits.includes(unit?.toLowerCase()) && quantity >= factor) {
        return {
          quantity: quantity / factor,
          unit: mainUnit,
        };
      }
    }
    // Default return original values
    return { quantity, unit };
  };


  const downloadExcelWithActualTotals = () => {
    const workbook = XLSX.utils.book_new();
    const worksheetData = [];

    // Header row with actual total columns for each restaurant
    const headers = ["Item"];
    data.forEach((restaurant) => {
      headers.push(restaurant.restaurantName);
      headers.push(`Actual Totals`);
    });
    headers.push("Total");
    headers.push("Grand Actual Total");

    worksheetData.push(headers);

    // Iterate over categories and items
    const sortedCategories = Object.keys(items).sort();

    sortedCategories.forEach((category) => {
      // Add category row (spanning columns)
      worksheetData.push([category, ...Array(data.length * 2 + 1).fill("")]);

      // Sort category items alphabetically by title
      const sortedCategoryItems = items[category].sort((a, b) => a.title.localeCompare(b.title));

      sortedCategoryItems.forEach((item) => {
        const row = [item.title];
        let rowTotal = 0;
        let rowGrandActualTotal = 0;

        data.forEach((restaurant) => {
          const foundItem = restaurant.itemQuantities.find(q => q.title === item.title) || {};
          const quantity = foundItem.quantity || 0;
          row.push(quantity);
          rowTotal += quantity;

          // Calculate actual total for THIS restaurant
          const invItem = inventoryItems.find(inv => inv.id === item.refId);
          let actualTotal = 0;
          if (invItem && invItem.oneUnitQuantity > 0) {
            actualTotal = quantity * invItem.oneUnitQuantity;
            rowGrandActualTotal += actualTotal;
          }

          const unit = invItem?.oneUnitQuantityUnit || invItem?.units;
          const converted = convertToMainUnit(actualTotal, unit);
          row.push(`${converted.quantity} ${converted.unit}`);
        });

        row.push(rowTotal);

        // Grand actual total for this item across all restaurants
        const invItem = inventoryItems.find(inv => inv.id === item.refId);
        const unit = invItem?.oneUnitQuantityUnit || invItem?.units;
        const convertedGrand = convertToMainUnit(rowGrandActualTotal, unit);
        row.push(`${convertedGrand.quantity} ${convertedGrand.unit}`);

        worksheetData.push(row);
      });
    });

    // Grand Total Row
    const totalRow = ["Total"];
    let grandTotal = 0;
    const restaurantActualTotals = [];

    data.forEach((restaurant) => {
      const restaurantTotal = restaurant.itemQuantities.reduce(
        (sum, item) => sum + item.quantity,
        0
      );
      totalRow.push(restaurantTotal);
      grandTotal += restaurantTotal;

      // Calculate actual total for this restaurant
      let restaurantActualTotal = 0;
      restaurant.itemQuantities.forEach(item => {
        const invItem = inventoryItems.find(inv => inv.id === item.refId);
        if (invItem && invItem.oneUnitQuantity > 0) {
          restaurantActualTotal += item.quantity * invItem.oneUnitQuantity;
        }
      });
      restaurantActualTotals.push(restaurantActualTotal);
    });

    // Add actual totals for each restaurant
    data.forEach((restaurant, index) => {
      const invItem = inventoryItems.find(inv => inv.id === restaurant.itemQuantities[0]?.refId);
      const unit = invItem?.oneUnitQuantityUnit || invItem?.units;
      const converted = convertToMainUnit(restaurantActualTotals[index], unit);
      totalRow.splice(totalRow.length - 1, 0, `${converted.quantity} ${converted.unit}`);
    });

    totalRow.push(grandTotal);

    // Grand actual total across all restaurants
    const grandActualTotal = restaurantActualTotals.reduce((sum, val) => sum + val, 0);
    const invItem = inventoryItems.find(inv => inv.id !== undefined);
    const unit = invItem?.oneUnitQuantityUnit || invItem?.units;
    const convertedGrandTotal = convertToMainUnit(grandActualTotal, unit);
    totalRow.push(`${convertedGrandTotal.quantity} ${convertedGrandTotal.unit}`);

    worksheetData.push(totalRow);

    // Convert to worksheet and save
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // Adjust column widths
    const colWidths = [];
    headers.forEach(() => colWidths.push({ wch: 18 }));
    worksheet['!cols'] = colWidths;

    XLSX.utils.book_append_sheet(workbook, worksheet, "Detailed Orders");
    XLSX.writeFile(workbook, "todays_invoices_detailed.xlsx");
  };

  const downloadPDFByRestaurant = () => {
    try {
      if (typeof jsPDF === 'undefined') {
        console.error('jsPDF is not available');
        alert('PDF generation is not available. Please try again.');
        return;
      }

      const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      if (typeof doc.autoTable !== 'function') {
        console.error('autoTable method is not available');
        alert('PDF table generation is not available. Please try again.');
        return;
      }

      let isFirstPage = true;

      // Generate a page for each restaurant
      data.forEach((restaurant, restaurantIndex) => {
        if (!isFirstPage) {
          doc.addPage();
        }
        isFirstPage = false;

        // Title
        doc.setFontSize(18);
        doc.text(`${restaurant.restaurantName}`, 15, 15);
        doc.setFontSize(10);
        doc.text(`Generated: ${new Date().toLocaleDateString('en-GB')}`, 15, 22);

        // Table headers
        const headers = ["Item", "Quantity", "Actual Total"];

        const rows = [];
        const sortedCategories = Object.keys(items).sort();
        let restaurantGrandTotal = 0;
        let restaurantGrandActualTotal = 0;

        // Flatten all items without categories
        sortedCategories.forEach((category) => {
          const sortedCategoryItems = items[category].sort((a, b) => a.title.localeCompare(b.title));

          sortedCategoryItems.forEach((item) => {
            const foundItem = restaurant.itemQuantities.find(q => q.title === item.title) || {};
            const quantity = foundItem.quantity || 0;

            if (quantity > 0) {
              restaurantGrandTotal += quantity;

              const invItem = inventoryItems.find(inv => inv.id === item.refId);
              let actualTotal = 0;
              let actualTotalStr = "-";

              if (invItem && invItem.oneUnitQuantity > 0) {
                actualTotal = quantity * invItem.oneUnitQuantity;
                restaurantGrandActualTotal += actualTotal;
                const unit = invItem.oneUnitQuantityUnit || invItem.units;
                const converted = convertToMainUnit(actualTotal, unit);
                actualTotalStr = `${converted.quantity} ${converted.unit}`;
              }

              rows.push({
                content: [item.title, quantity.toString(), actualTotalStr],
                isCategory: false,
                isGrandTotal: false
              });
            }
          });
        });

        // Add grand total row
        const invItem = inventoryItems[0];
        const unit = invItem?.oneUnitQuantityUnit || invItem?.units || "";
        const convertedGrandTotal = convertToMainUnit(restaurantGrandActualTotal, unit);

        rows.push({
          content: ["TOTAL", restaurantGrandTotal.toString(), `${convertedGrandTotal.quantity} ${convertedGrandTotal.unit}`],
          isGrandTotal: true
        });

        // Draw table
        doc.autoTable({
          head: [headers],
          body: rows.map(row => row.content),
          startY: 28,
          theme: 'grid',
          styles: {
            fontSize: 11,
            cellPadding: 3,
            font: 'helvetica',
            lineColor: [0, 0, 0],
            lineWidth: 0.3,
          },
          headStyles: {
            fillColor: [200, 200, 200],
            textColor: [0, 0, 0],
            fontStyle: 'bold',
            fontSize: 11,
            lineColor: [0, 0, 0],
            lineWidth: 0.4,
          },
          bodyStyles: {
            font: 'helvetica',
            lineColor: [0, 0, 0],
            lineWidth: 0.3,
          },
          alternateRowStyles: {
            fillColor: [245, 245, 245],
            lineColor: [0, 0, 0],
            lineWidth: 0.3,
          },
          willDrawCell: (data) => {
            if (data && data.row && typeof data.row.index !== 'undefined') {
              const rowIndex = data.row.index;
              const rowInfo = rows[rowIndex];

              if (rowInfo && rowInfo.isGrandTotal) {
                doc.setFillColor(220, 220, 220);
                doc.rect(data.cell.x, data.cell.y, data.cell.width, data.cell.height, 'F');
                doc.setTextColor(0, 0, 0);
                doc.setFontSize(11);
                doc.setFont("helvetica", "bold");
                doc.setDrawColor(0, 0, 0);
                doc.setLineWidth(0.5);
                doc.rect(data.cell.x, data.cell.y, data.cell.width, data.cell.height);
              }
            }
          }
        });
      });

      doc.save("todays_orders_by_restaurant.pdf");

    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF: ' + error.message);
    }
  };


  const downloadExcel = () => {
    const workbook = XLSX.utils.book_new();
    const worksheetData = [];

    // Header row
    const headers = ["Item", ...data.map((d) => d.restaurantName), "Total", "Actual Total Qty"];
    worksheetData.push(headers);

    // Iterate over categories and items
    const sortedCategories = Object.keys(items).sort();

    sortedCategories.forEach((category) => {
      // Add category row (spanning columns)
      worksheetData.push([category, ...Array(data.length + 2).fill("")]); // +2 includes Total & Actual Qty

      // Sort category items alphabetically by title
      const sortedCategoryItems = items[category].sort((a, b) => a.title.localeCompare(b.title));

      sortedCategoryItems.forEach((item) => {
        const row = [item.title];
        let rowTotal = 0;
        let rowActualTotal = 0;

        data.forEach((restaurant) => {
          const foundItem = restaurant.itemQuantities.find(q => q.title === item.title) || {};
          const quantity = foundItem.quantity || 0;
          row.push(quantity);
          rowTotal += quantity;

          const invItem = inventoryItems.find(inv => inv.id === item.refId);
          if (invItem && invItem.oneUnitQuantity > 0) {
            rowActualTotal += quantity * invItem.oneUnitQuantity;
          }
        });

        row.push(rowTotal);
        if (rowActualTotal && item.refId) {
          const inventoryItem = inventoryItems.find(inv => inv.id === item.refId);
          const unit = inventoryItem?.oneUnitQuantityUnit || inventoryItem?.units;
          const converted = convertToMainUnit(rowActualTotal, unit);
          row.push(`${converted.quantity} ${converted.unit}`);
        } else {
          row.push("-");
        }

        worksheetData.push(row);
      });
    });



    // Grand Total Row
    const totalRow = ["Total"];
    let grandTotal = 0;

    data.forEach((restaurant) => {
      const restaurantTotal = restaurant.itemQuantities.reduce(
        (sum, item) => sum + item.quantity,
        0
      );
      totalRow.push(restaurantTotal);
      grandTotal += restaurantTotal;
    });

    totalRow.push(grandTotal);
    worksheetData.push(totalRow);

    // Convert to worksheet and save
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Invoices");
    XLSX.writeFile(workbook, "todays_invoices.xlsx");
  };


  const downloadPDF = () => {
    try {
      // Check if jsPDF is available
      if (typeof jsPDF === 'undefined') {
        console.error('jsPDF is not available');
        alert('PDF generation is not available. Please try again.');
        return;
      }

      const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a3",
      });

      // Check if autoTable method exists
      if (typeof doc.autoTable !== 'function') {
        console.error('autoTable method is not available');
        alert('PDF table generation is not available. Please try again.');
        return;
      }

      const headers = ["Item", ...data.map((d) => d.restaurantName), "Total", "Actual Total Qty"];
      const rows = [];

      // Process data with category markers
      const sortedCategories = Object.keys(items).sort();

      sortedCategories.forEach((category) => {
        // Add category row first
        rows.push({
          content: [category, ...Array(data.length + 2).fill("")], // +2 for Total and Actual Qty columns
          isCategory: true
        });

        // Sort items alphabetically in this category
        const sortedCategoryItems = items[category].sort((a, b) => a.title.localeCompare(b.title));

        // Add each item row
        sortedCategoryItems.forEach((item) => {
          const row = [item.title];
          let rowTotal = 0;
          let rowActualTotal = 0;

          data.forEach((restaurant) => {
            const quantity = restaurant.itemQuantities.find((q) => q.title === item.title)?.quantity || 0;
            row.push(quantity.toString());
            rowTotal += quantity;

            // Find inventory record for unit calculation
            const invItem = inventoryItems.find(inv => inv.id === item.refId);
            if (invItem && invItem.oneUnitQuantity > 0) {
              rowActualTotal += quantity * invItem.oneUnitQuantity;
            }
          });

          row.push(rowTotal.toString());

          if (item.refId) {
            const inventoryItem = inventoryItems.find(inv => inv.id === item.refId);
            const unit = inventoryItem?.oneUnitQuantityUnit || inventoryItem?.units;
            const converted = convertToMainUnit(rowActualTotal, unit);
            row.push(`${converted.quantity} ${converted.unit}`);
          } else {
            row.push("-");
          }

          rows.push({
            content: row,
            isCategory: false
          });
        });
      });


      // Simple title
      doc.setFontSize(26);
      doc.text("Today's Orders Grid", 170, 15);

      // Simple table with darker and thicker grid lines
      doc.autoTable({
        head: [headers],
        body: rows.map(row => row.content),
        startY: 20,
        theme: 'grid',
        styles: {
          fontSize: 13,
          cellPadding: 3,
          font: 'helvetica',
          lineColor: [0, 0, 0], // Pure black grid lines
          lineWidth: 0.3, // Thicker grid lines
        },
        headStyles: {
          fillColor: [200, 200, 200],
          textColor: [0, 0, 0],
          fontStyle: 'bold',
          fontSize: 12,
          font: 'helvetica',
          lineColor: [0, 0, 0], // Black borders for header
          lineWidth: 0.4, // Even thicker borders for header
        },
        bodyStyles: {
          font: 'helvetica',
          lineColor: [0, 0, 0], // Black borders for body
          lineWidth: 0.3, // Thicker borders for body
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245], // Light gray for alternate rows
          lineColor: [0, 0, 0],
          lineWidth: 0.3,
        },
        // Safe way to style category rows
        willDrawCell: (data) => {
          // Check if data exists and has row property
          if (data && data.row && typeof data.row.index !== 'undefined') {
            const rowIndex = data.row.index;
            const isCategory = rows[rowIndex] && rows[rowIndex].isCategory;

            if (isCategory) {
              // Style category rows with darker background and thicker borders
              doc.setFillColor(180, 180, 180); // Darker gray
              doc.rect(data.cell.x, data.cell.y, data.cell.width, data.cell.height, 'F');

              doc.setTextColor(0, 0, 0);
              doc.setFontSize(14);
              doc.setFont("helvetica", "bold");

              doc.text(
                data.cell.raw ? String(data.cell.raw) : "",
                data.cell.x + 4,
                data.cell.y + data.cell.height / 2 + 2
              );



              // Draw thicker border around category cells
              doc.setDrawColor(0, 0, 0); // Black border
              doc.setLineWidth(0.5); // Thicker border for categories
              doc.rect(data.cell.x, data.cell.y, data.cell.width, data.cell.height);
            }
          }
        }
      });

      doc.save("todays_orders.pdf");

    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF: ' + error.message);
    }
  };


  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={300}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (!invoices || invoices.length === 0) {
    return (
      <Box p={2}>
        <Alert
          severity="warning"
          icon={<WarningIcon />}
          sx={{
            "& .MuiAlert-icon": {
              color: theme.palette.primary.main,
            },
          }}
        >
          <AlertTitle>No Orders Found</AlertTitle>
          There are no orders for today. New orders will appear here as they
          come in.
        </Alert>
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Paper
        elevation={3}
        sx={{
          p: 3,
          marginLeft: "3.5%",
          marginTop: "15px",
          maxWidth: "95%",
        }}
      >
        {/* **UPDATED TITLE SECTION WITH REFRESH BUTTON** */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            flexWrap: 'wrap',
            gap: 2
          }}
        >
          <Typography
            variant="h4"
            color="text.primary"
            sx={{
              fontWeight: "bold",
              flex: 1,
              minWidth: '300px'
            }}
          >
            Today's Orders Grid By Restaurant
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontStyle: 'italic' }}
          >
            📍 UK Time Range: {getTodayRangeText().start} to {getTodayRangeText().end}
          </Typography>

          <Button
            variant="outlined"
            color="primary"
            onClick={handleRefresh}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
            sx={{
              minWidth: 120,
              height: 40,
              borderRadius: 2,
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: theme.palette.primary.main,
                color: 'white',
              }
            }}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </Button>
        </Box>


        {/* Table Container */}
        <Box
          sx={{
            width: "100%",
            overflowX: "auto",
            position: "relative",
            "&::-webkit-scrollbar": {
              height: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#f1f1f1",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#888",
              borderRadius: "4px",
            },
          }}
          onScroll={handleScroll}
        >
          <table
            id="invoice-table"
            style={{
              minWidth: "100%",
              width: "max-content",
              borderCollapse: "collapse",
              borderSpacing: 0,
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    position: "sticky",
                    left: 0,
                    zIndex: 3,
                    backgroundColor: "#e8f5e9",
                    color: "#1976d2",
                    padding: "16px",
                    border: "1px solid #ffffff",
                    minWidth: "150px",
                    maxWidth: "250px",
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                  }}
                >
                  Item
                </th>
                {data.map((restaurant, index) => (
                  <th
                    key={index}
                    style={{
                      backgroundColor: "#e8f5e9",
                      color: "#1976d2",
                      padding: "16px",
                      border: "1px solid #ffffff",
                      minWidth: "150px",
                      maxWidth: "200px",
                      whiteSpace: "normal",
                      wordWrap: "break-word",
                    }}
                  >
                    {restaurant.restaurantName}
                  </th>
                ))}
                <th
                  style={{
                    backgroundColor: "#e8f5e9",
                    color: "#1976d2",
                    padding: "16px",
                    border: "1px solid #ffffff",
                    minWidth: "70px",
                    position: "sticky",
                    right: 0,
                    zIndex: 3,
                  }}
                >
                  Total
                </th>
                <th
                  style={{
                    backgroundColor: "#e8f5e9",
                    color: "#1976d2",
                    padding: "16px",
                    border: "1px solid #ffffff",
                    minWidth: "70px",
                    position: "sticky",
                    right: 0,
                    zIndex: 3,
                  }}
                >
                  Actual Total quantity
                </th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(items).map(([category, categoryItems], catIndex) => (
                <React.Fragment key={catIndex}>
                  {/* Category Divider with fixed position */}
                  <tr>
                    <td
                      colSpan={data.length + 3}
                      style={{
                        position: "relative",
                        padding: 0,
                        height: "40px",
                        backgroundColor: "#f8f9fa",
                      }}
                    >
                      {/* Fixed Category Label - this will stay in place during scroll */}
                      <div
                        className="category-label"
                        style={{
                          position: "absolute",
                          left: 0,
                          top: 0,
                          bottom: 0,
                          zIndex: 4,
                          backgroundColor: "#f8f9fa",
                          fontWeight: "bold",
                          textAlign: "left",
                          padding: "10px",
                          border: "1px solid #ddd",
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {category}
                      </div>
                    </td>
                  </tr>

                  {/* Render Items in Category */}
                  {categoryItems.map((item, index) => {
                    let current = inventoryItems.find((inventoryItem) => inventoryItem.id === item.refId);


                    console.log(current, "its actial item");
                    const total = data.reduce((sum, restaurant) => {
                      const quantity =
                        restaurant.itemQuantities.find(
                          (q) => q.title === item.title
                        )?.quantity || 0;
                      return sum + quantity;
                    }, 0);

                    return (
                      <tr key={index}>
                        {/* Sticky Item Column */}
                        <td
                          style={{
                            padding: "16px",
                            border: "1px solid #ddd",
                            fontWeight: "500",
                            position: "sticky",
                            left: 0,
                            backgroundColor: "#fff",
                            zIndex: 2,
                          }}
                        >
                          {item.title}
                        </td>

                        {/* Restaurant Data */}
                        {data.map((restaurant, i) => (
                          <td
                            key={i}
                            style={{
                              padding: "16px",
                              border: "1px solid #ddd",
                              textAlign: "center",
                            }}
                          >
                            {restaurant.itemQuantities.find(
                              (q) => q.title === item.title
                            )?.quantity || "-"}
                          </td>
                        ))}

                        {/* Sticky Total Column */}
                        <td
                          style={{
                            padding: "16px",
                            border: "1px solid #ddd",
                            fontWeight: "bold",
                            textAlign: "center",
                            position: "sticky",
                            right: 0,
                            backgroundColor: "#fff",
                            zIndex: 2,
                          }}
                        >
                          {total}
                        </td>
                        <td
                          style={{
                            padding: "16px",
                            border: "1px solid #ddd",
                            fontWeight: "bold",
                            textAlign: "center",
                            position: "sticky",
                            right: 0,
                            backgroundColor: "#fff",
                            zIndex: 2,
                          }}
                        >
                          {current && current.oneUnitQuantity > 0
                            ? (() => {
                              const converted = convertToMainUnit(current.oneUnitQuantity * total, current.oneUnitQuantityUnit || current.units);
                              return `${converted.quantity} ${converted.unit}`;
                            })()
                            : "-"}


                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}

              {/* Grand Total Row */}
              <tr style={{ backgroundColor: "#f1f1f1", fontWeight: "bold" }}>
                <td
                  style={{
                    padding: "16px",
                    border: "1px solid #ddd",
                    position: "sticky",
                    left: 0,
                    backgroundColor: "#f1f1f1",
                    zIndex: 2,
                  }}
                >
                  Total
                </td>

                {data.map((restaurant, i) => {
                  const grandTotal = restaurant.itemQuantities.reduce(
                    (sum, item) => sum + item.quantity,
                    0
                  );
                  return (
                    <td
                      key={i}
                      style={{
                        padding: "16px",
                        border: "1px solid #ddd",
                        textAlign: "center",
                        backgroundColor: "#f1f1f1",
                      }}
                    >
                      {grandTotal}
                    </td>
                  );
                })}

                <td
                  style={{
                    padding: "16px",
                    border: "1px solid #ddd",
                    fontWeight: "bold",
                    textAlign: "center",
                    position: "sticky",
                    right: 0,
                    backgroundColor: "#f1f1f1",
                    zIndex: 2,
                  }}
                >
                  {data.reduce(
                    (sum, restaurant) =>
                      sum +
                      restaurant.itemQuantities.reduce(
                        (s, i) => s + i.quantity,
                        0
                      ),
                    0
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 3, flexWrap: "wrap" }}>
          <Button
            variant="contained"
            color="primary"
            onClick={downloadPDF}
            sx={{ minWidth: 150 }}
            startIcon={<PictureAsPdfIcon />}
          >
            Download PDF (Combined)
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={downloadPDFByRestaurant}
            sx={{ minWidth: 150 }}
            startIcon={<PictureAsPdfIcon />}
          >
            Download PDF (By Restaurant)
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={downloadExcel}
            sx={{ minWidth: 150 }}
            startIcon={<TableChartIcon />}
          >
            Download Excel
          </Button>
          <Button
            variant="contained"
            color="info"
            onClick={downloadExcelWithActualTotals}
            sx={{ minWidth: 150 }}
            startIcon={<TableChartIcon />}
          >
            Download Excel (Detailed)
          </Button>
        </Box>

      </Paper>
    </ThemeProvider>
  );
};

export default TodayInvoicesGrid;